import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Autocomplete, Avatar, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, Stack, TextField, Typography,
} from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import { APIURL } from '../../config';

const today = () => new Date().toISOString().slice(0, 10);

const ResidenceMembers = ({ residence, assignments, rooms, user, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [room, setRoom] = useState(null);
  const [admissionDate, setAdmissionDate] = useState(today());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const availableRooms = useMemo(
    () => rooms.filter((item) => Number(item.current_vacancy) > 0),
    [rooms]
  );
  const assignedCustomerIds = useMemo(
    () => new Set(assignments.map((assignment) => assignment.customer_id)),
    [assignments]
  );
  const availableCustomers = useMemo(
    () => customers.filter((item) => !assignedCustomerIds.has(item.cus_id)),
    [customers, assignedCustomerIds]
  );

  useEffect(() => {
    if (!open) return;
    setError('');
    axios.get(`${APIURL}/customers/v2/associated`, {
      params: { company_id: user.comp_id, employee_id: user.emp_id, type: user.type },
    }).then(({ data }) => setCustomers(Array.isArray(data) ? data : []))
      .catch(() => setError('Unable to load customers.'));
  }, [open, user.comp_id, user.emp_id, user.type]);

  const close = () => {
    setOpen(false);
    setCustomer(null);
    setRoom(null);
    setAdmissionDate(today());
    setError('');
  };

  const addResident = async () => {
    if (!customer || !room || !admissionDate) {
      setError('Customer, room, and admission date are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post(`${APIURL}/residences/assignments`, {
        company_id: user.comp_id,
        customer_id: customer.cus_id,
        room_id: room.room_id,
        admission_date: admissionDate,
        emp_id: user.emp_id,
      });
      close();
      await onChanged();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to add this resident.');
    } finally {
      setSaving(false);
    }
  };

  const removeResident = async (assignment) => {
    if (!window.confirm(`Remove ${assignment.cus_name} from ${residence.residence_name}?`)) return;
    try {
      await axios.post(`${APIURL}/residences/assignments/${assignment.assignment_id}/discharge`, {
        company_id: user.comp_id,
        move_out_date: today(),
        emp_id: user.emp_id,
      });
      await onChanged();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to remove this resident.');
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<PersonAddOutlinedIcon />}
          onClick={() => setOpen(true)}
          disabled={!rooms.length}
        >
          Add Resident
        </Button>
      </Stack>
      {!rooms.length && <Alert severity="info" sx={{ mb: 2 }}>Add a room before assigning residents to this residence.</Alert>}
      {error && !open && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!assignments.length && rooms.length > 0 && <Alert severity="info">No customers are currently assigned to this residence.</Alert>}
      <Grid container spacing={2}>
        {assignments.map((assignment) => (
          <Grid item xs={12} sm={6} md={4} key={assignment.assignment_id}>
            <Card elevation={0} sx={{ height: '100%', border: '1px solid #dce8f5', borderRadius: 2, overflow: 'hidden', transition: 'transform 0.15s ease, box-shadow 0.15s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(30, 95, 145, 0.12)' } }}>
              <Box sx={{ height: 5, bgcolor: '#3FA7D6' }} />
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#EBF8FF', color: '#1976d2', fontWeight: 700 }}>
                    {(assignment.cus_name || '?').trim().charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>{assignment.cus_name}</Typography>
                    <Chip label="Current Member" size="small" color="success" variant="outlined" sx={{ mt: 0.5, height: 22 }} />
                  </Box>
                </Stack>
                <Box sx={{ p: 1.25, bgcolor: '#F7FBFE', borderRadius: 1.5, mb: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <HotelOutlinedIcon sx={{ color: '#3FA7D6', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Assigned room</Typography>
                      <Typography variant="body2" fontWeight={600}>Room {assignment.room_identifier}{assignment.room_type ? ` · ${assignment.room_type}` : ''}</Typography>
                    </Box>
                  </Stack>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Admitted {new Date(assignment.admission_date).toLocaleDateString()}
                </Typography>
                <Button fullWidth variant="outlined" color="error" size="small" startIcon={<PersonRemoveOutlinedIcon />} onClick={() => removeResident(assignment)}>
                  Remove from Residence
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Add Resident to {residence.residence_name}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              options={availableCustomers}
              value={customer}
              onChange={(event, value) => setCustomer(value)}
              getOptionLabel={(option) => option.cus_name || 'Unnamed Customer'}
              isOptionEqualToValue={(option, value) => option.cus_id === value.cus_id}
              renderInput={(params) => <TextField {...params} required label="Customer" />}
            />
            <Autocomplete
              options={availableRooms}
              value={room}
              onChange={(event, value) => setRoom(value)}
              getOptionLabel={(option) => `${option.room_identifier} (${option.current_vacancy} available)`}
              isOptionEqualToValue={(option, value) => option.room_id === value.room_id}
              renderInput={(params) => <TextField {...params} required label="Room" />}
            />
            <TextField
              required
              type="date"
              label="Admission Date"
              value={admissionDate}
              onChange={(event) => setAdmissionDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={addResident} disabled={saving || !customer || !room || !admissionDate}>
            {saving ? 'Adding…' : 'Add Resident'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResidenceMembers;
